import { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, XCircle, Sparkles, Inbox, Loader2, Clock } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import type { FaturaStatusSyncRequest } from '../../types';
import { agruparSincronizacaoStatus, type GrupoSincronizacaoStatus as Grupo } from './lib/agruparSincronizacaoStatus';

// Fila de aprovação de "Sincronizar status sem gerar boleto" — ver
// docs/superpowers/specs/2026-09-02-sincronizacao-status-fatura-design.md.

const formatCurrency = (v: number) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatDateTimeBR = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export default function SincronizacaoStatusView() {
    const { user, profile } = useAuth();
    const { addToast } = useToast();

    const podeDecidir = ['adm', 'gerente'].includes(profile?.role || '');

    const [loading, setLoading] = useState(true);
    const [linhas, setLinhas] = useState<FaturaStatusSyncRequest[]>([]);
    const [processando, setProcessando] = useState<string | null>(null);
    const [rejeitandoChave, setRejeitandoChave] = useState<string | null>(null);
    const [motivoRejeicaoTexto, setMotivoRejeicaoTexto] = useState('');

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('fatura_status_sync_requests')
                .select('*, fatura:faturas(id, uc_codigo, cliente_nome, mes_referencia, valor_boleto_vigor, vencimento_boleto)')
                .order('solicitado_em', { ascending: false })
                .limit(300);
            if (error) throw error;
            setLinhas((data || []) as FaturaStatusSyncRequest[]);
        } catch (err) {
            console.error(err);
            addToast('Erro ao carregar fila de sincronização', 'error');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const grupos = agruparSincronizacaoStatus(linhas);
    const pendentes = grupos.filter(g => g.decisao === 'pendente');
    const decididos = grupos.filter(g => g.decisao !== 'pendente');

    const aprovar = async (grupo: Grupo) => {
        setProcessando(grupo.chave);
        try {
            const ids = grupo.itens.map(i => i.id);
            const faturaIds = grupo.itens.map(i => i.fatura_id);
            const statusAlvo = grupo.itens[0].status_alvo;

            const { error: errFaturas } = await supabase
                .from('faturas')
                .update({ status_boleto_vigor: statusAlvo })
                .in('id', faturaIds);
            if (errFaturas) throw errFaturas;

            const { error: errReq } = await supabase
                .from('fatura_status_sync_requests')
                .update({
                    decisao: 'aprovado',
                    decidido_por_id: user?.id,
                    decidido_por_nome: profile?.full_name || user?.email || 'Gerente',
                    decidido_em: new Date().toISOString(),
                })
                .in('id', ids);
            if (errReq) throw errReq;

            await supabase.from('notifications').insert({
                user_id: grupo.itens[0].solicitado_por_id,
                type: 'fatura_status_sync_decidido',
                title: 'Sincronização de status aprovada',
                content: `${profile?.full_name || 'Um gerente'} aprovou a sincronização de ${grupo.itens.length} fatura(s).`,
                link: '/financeiro/sincronizacao-status',
                read: false,
            });

            addToast('Sincronização aprovada — status atualizado.', 'success');
            carregar();
        } catch (err: any) {
            console.error(err);
            addToast(`Erro ao aprovar: ${err.message || err}`, 'error');
        } finally {
            setProcessando(null);
        }
    };

    const confirmarRejeicao = async (grupo: Grupo) => {
        const motivo = motivoRejeicaoTexto.trim();
        if (!motivo) {
            addToast('Informe o motivo da rejeição — é o que o analista vai ver.', 'error');
            return;
        }
        setProcessando(grupo.chave);
        try {
            const ids = grupo.itens.map(i => i.id);
            const { error } = await supabase
                .from('fatura_status_sync_requests')
                .update({
                    decisao: 'rejeitado',
                    decidido_por_id: user?.id,
                    decidido_por_nome: profile?.full_name || user?.email || 'Gerente',
                    decidido_em: new Date().toISOString(),
                    motivo_rejeicao: motivo,
                })
                .in('id', ids);
            if (error) throw error;

            await supabase.from('notifications').insert({
                user_id: grupo.itens[0].solicitado_por_id,
                type: 'fatura_status_sync_decidido',
                title: 'Sincronização de status rejeitada',
                content: `${profile?.full_name || 'Um gerente'} rejeitou o pedido: "${motivo}"`,
                link: '/financeiro/sincronizacao-status',
                read: false,
            });

            addToast('Solicitação rejeitada.', 'success');
            setRejeitandoChave(null);
            setMotivoRejeicaoTexto('');
            carregar();
        } catch (err: any) {
            console.error(err);
            addToast(`Erro ao rejeitar: ${err.message || err}`, 'error');
        } finally {
            setProcessando(null);
        }
    };

    const renderGrupo = (grupo: Grupo, decidido: boolean) => {
        const total = grupo.itens.reduce((s, i) => s + (Number(i.fatura?.valor_boleto_vigor) || 0), 0);
        const clienteNome = grupo.itens[0].fatura?.cliente_nome || '';

        return (
            <div key={grupo.chave} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm space-y-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{grupo.solicitanteNome}</p>
                        <p className="text-xs text-gray-400 truncate">solicitou {formatDateTimeBR(grupo.solicitadoEm)} · {clienteNome}</p>
                    </div>
                    {grupo.vickFazSentido !== null && (
                        <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shrink-0 ${
                            grupo.vickFazSentido
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        }`}>
                            <Sparkles size={11} /> {grupo.vickFazSentido ? 'Vick: faz sentido' : 'Vick sinalizou'}
                        </span>
                    )}
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300">
                    <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Motivo</span>
                    {grupo.motivo}
                </div>

                {grupo.vickFazSentido === false && grupo.vickComentario && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2.5 text-xs text-amber-800 dark:text-amber-300">
                        <span className="block text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-1">
                            Vick sinalizou antes do envio — enviado mesmo assim
                        </span>
                        {grupo.vickComentario}
                    </div>
                )}

                <div className="flex flex-wrap gap-1.5">
                    {grupo.itens.map(i => (
                        <span key={i.id} className="text-xs bg-gray-100 dark:bg-gray-700 rounded-md px-2 py-1 text-gray-600 dark:text-gray-300">
                            {i.fatura?.mes_referencia} <span className="text-gray-400">{formatCurrency(i.fatura?.valor_boleto_vigor || 0)}</span>
                        </span>
                    ))}
                </div>
                <p className="text-xs text-gray-400">Total: <span className="font-bold text-gray-700 dark:text-gray-200">{formatCurrency(total)}</span></p>

                {!decidido && podeDecidir && (
                    <>
                        <div className="flex gap-2 pt-1">
                            <button
                                onClick={() => aprovar(grupo)}
                                disabled={processando === grupo.chave}
                                className="px-3 py-1.5 text-xs font-bold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
                            >
                                {processando === grupo.chave ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Aprovar
                            </button>
                            <button
                                onClick={() => {
                                    if (rejeitandoChave === grupo.chave) {
                                        setRejeitandoChave(null);
                                    } else {
                                        // Cada card tem seu próprio motivo: sem isto, o texto digitado
                                        // para rejeitar um pedido ficava pré-preenchido ao abrir o painel
                                        // de rejeição de outro pedido pendente na mesma fila.
                                        setRejeitandoChave(grupo.chave);
                                        setMotivoRejeicaoTexto('');
                                    }
                                }}
                                disabled={processando === grupo.chave}
                                className="px-3 py-1.5 text-xs font-bold rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 flex items-center gap-1.5"
                            >
                                <XCircle size={13} /> Rejeitar
                            </button>
                        </div>
                        {rejeitandoChave === grupo.chave && (
                            <div className="pt-1 space-y-2">
                                <textarea
                                    value={motivoRejeicaoTexto}
                                    onChange={(e) => setMotivoRejeicaoTexto(e.target.value)}
                                    placeholder="Por que está rejeitando? (o analista vê essa mensagem)"
                                    rows={2}
                                    autoFocus
                                    className="w-full text-xs px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 outline-none focus:ring-2 focus:ring-primary/20"
                                />
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => { setRejeitandoChave(null); setMotivoRejeicaoTexto(''); }}
                                        disabled={processando === grupo.chave}
                                        className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={() => confirmarRejeicao(grupo)}
                                        disabled={processando === grupo.chave}
                                        className="px-3 py-1.5 text-xs font-bold rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                                    >
                                        {processando === grupo.chave ? 'Rejeitando...' : 'Confirmar rejeição'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {!decidido && !podeDecidir && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 pt-1">
                        <Clock size={12} /> Aguardando decisão de um gerente ou administrador.
                    </div>
                )}

                {decidido && (
                    <div className="flex items-center gap-2 text-xs">
                        {grupo.decisao === 'aprovado' ? (
                            <span className="font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 flex items-center gap-1">
                                <CheckCircle2 size={12} /> Aprovado
                            </span>
                        ) : (
                            <span className="font-bold px-2.5 py-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 flex items-center gap-1">
                                <XCircle size={12} /> Rejeitado
                            </span>
                        )}
                        <span className="text-gray-400">por {grupo.decididoPorNome} em {formatDateTimeBR(grupo.decididoEm)}</span>
                    </div>
                )}
                {decidido && grupo.decisao === 'rejeitado' && grupo.motivoRejeicao && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 italic">"{grupo.motivoRejeicao}"</p>
                )}
            </div>
        );
    };

    return (
        <div className="p-4 md:p-6 space-y-6">
            <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">Sincronização de Status</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Pedidos para trocar o status de faturas já renegociadas por fora do sistema, sem gerar boleto novo
                    {!podeDecidir && ' — aprovação é feita por gerente/adm'}.
                </p>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                    <Loader2 className="animate-spin text-primary mb-4" size={40} />
                    <p className="font-bold text-sm animate-pulse">Carregando fila de sincronização...</p>
                </div>
            ) : (
                <>
                    <div>
                        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">
                            Pendentes {pendentes.length > 0 && <span className="ml-1 text-primary">({pendentes.length})</span>}
                        </h2>
                        {pendentes.length === 0 ? (
                            <div className="text-center py-10 text-gray-400 bg-gray-50 dark:bg-gray-900 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                                <Inbox className="mx-auto mb-2" size={24} />
                                <p className="text-sm">Nenhuma solicitação pendente.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">{pendentes.map(g => renderGrupo(g, false))}</div>
                        )}
                    </div>

                    {decididos.length > 0 && (
                        <div>
                            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Decididas</h2>
                            <div className="space-y-3">{decididos.map(g => renderGrupo(g, true))}</div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
